import React, { PropTypes } from 'react'
import { Button } from './ui'

/**
 * Get a property from an object optionally following a path and also providing a default.
 *
 * The `key` parameter can take a dot-separated path to a deeply-nested key.
 *
 * `null` values are considered 'empty' for the purposes of this function, and the value of
 * `defaultValue` will be returned in the case that `null` is found. Of course, the default default
 * is `null` anyway, so there you go. This is only true of `null`: `0`, `''` and `false` are all
 * considered to be 'filled' values and will be returned as-is.
 *
 * Example calling:
 *
 *     let obj = { defined: 'string', empty: null }
 *
 *     getProperty(obj, 'defined') => 'string'
 *     getProperty(obj, 'undefined') => null
 *
 *     getProperty(obj, 'defined', 'default') => 'string'
 *     getProperty(obj, 'undefined', 'default') => 'default'
 *
 *     // while `empty` is defined, it is set to null so is considered 'empty'
 *     getProperty(obj, 'empty') => null // null here being the default return, not the value stored
 *     getProperty(obj, 'empty', 'default') => 'default'
 *
 * @param {Object} object The object to find the key on.
 * @param {string} key The key to search for on the object.
 * @param {mixed} defaultValue The value to return if the key isn't found.
 * @returns {mixed} The value of object.key or defaultValue depending on the rules above.
 */
export function getProperty(object, key, defaultValue = null) {
    if (! object) {
        return defaultValue
    }

    // if no key, we just care about the object itself
    if (! key) {
        return object
    }

    // are we using complex path components (i.e. dot-notation)?
    if (key.indexOf('.') !== -1) {
        // take the first 'path component' from the key
        const path = key.split('.')
        const currentKey = path.shift()

        // now getting the value of the object[first-path-part] to kick off recusion
        const currentValue = getProperty(object, currentKey, defaultValue)

        // rebuild the key leaving out the first part we already used
        const restOfKey = path.join('.')

        // now recurse by getting the value of [rest-of-key] from our
        // [first-path-part] value
        return getProperty(currentValue, restOfKey, defaultValue)
    }

    // now the actual reason for this funciton - does the key exist on object and is it 'filled'?
    if (object.hasOwnProperty(key) && (object[key] !== null) && (typeof object[key] !== 'undefined')) {
        return object[key]
    }

    return defaultValue
}

/**
 * Get the numeric value of a given field in an object.
 *
 * Basically calls parseFloat on the value contained at object[field] but silently ignoring
 * any errors like the field not existing, being non-numeric, etc., and, in those cases, returning 0.
 *
 * @param {object} object - The object to inspect for `field`.
 * @param {mixed} field - The field on `object` from which to get the value.
 * @return {float} The value of the field as best we can parse it.
 */
export function getNumericValueOf(object, field) {
    return (parseFloat(getProperty(object, field, 0)) || 0)
}

/**
 * Copy keys from an existing object only if they already exist.
 *
 * @param {object} data - The source object.
 * @param {object|string[]} keys - The keys to attempt to copy out of the oirce object, either as an
 *                                 array, or an object where the keys are the keys to pluck out of
 *                                 the source object and the values are the keys to use for the same
 *                                 values in the returned object.
 *
 * @returns {object} A new object with just the copied data.
 */
export function filterObject(data, keys) {
    const newData = {}

    // If keys was passed in as an array, turn it into an object where each key/value pair are the
    // same (as we don't want to rename the keys) like this:
    // `[ val1, val2, etc. ] -> { val1: val1, val2: val2, etc. }`
    if (Array.isArray(keys)) {
        keys = keys.reduce((acc, item) => { acc[item] = item; return acc }, {})
    }

    // Now go through the list of keys, plucking the value from the original data and setting that
    // value against the new key value on the new data object.
    for (const key in keys) {
        if (data.hasOwnProperty(key) && data[key] !== null) {
            newData[keys[key]] = data[key]
        }
    }

    return newData
}

export function filterObjectKeys(obj, keys, mode = 'include') {
    const newObject = {}

    for (const key in obj) {
        if (keys.includes(key)) {
            if (mode === 'include') {
                newObject[key] = obj[key]
            }
        } else {
            if (mode === 'exclude') {
                newObject[key] = obj[key]
            }
        }
    }

    return newObject
}

export function sendPatchRequest(field) {
    this.props.updateValueAndSendPatchRequest({
        [field]: this.props.data[field],
    })
}

export function normaliseMap(mapping, identifier = 'uuid', label = 'name', extras) {
    // first off, ensure the mapping is something we can work with
    if (! mapping) {
        return []
    }

    // now map it to something we want to work with
    return mapping.map((item) => {
        const extraProperties = {}

        if (extras) {
            for (const key in extras) {
                extraProperties[key] = getProperty(item, extras[key])
            }
        }

        return {
            ...extraProperties,
            value: item[identifier],
            label: item[label],
        }
    })
}

function getLeaves(struct) {
    let items = []

    if (struct) {
        if (Array.isArray(struct)) {
            for (const item of struct) {
                items = items.concat(getLeaves(item))
            }
        } else if (struct === Object(struct)) {
            for (const item of Object.values(struct)) {
                items = items.concat(getLeaves(item))
            }
        } else {
            items.push(struct)
        }
    }

    return items.sort()
}

class ErrorModal extends React.Component {
    componentDidMount() {
        if (this.props.modalHook) {
            this.props.modalHook({
                title: 'Oops, we have a few errors...',
                footer: (
                    <div className="dialog-footer__actions dialog-footer__actions--center">
                        <Button
                            autoFocus
                            buttonOnly
                            onClick={ this.props.closeModal }
                            status="delete"
                        >
                            Close
                        </Button>
                    </div>
                ),
            })
        }
    }

    render() {
        return (
            <div>
                <p>Please review the following errors:</p>

                <ul>
                    { this.props.errors.map((item, index) => (
                        <li key={ index }>
                            { item }
                        </li>
                    )) }
                </ul>
            </div>
        )
    }
}
ErrorModal.propTypes = {
    closeModal: PropTypes.func,
    errors: PropTypes.array.isRequired,
    modalHook: PropTypes.func,
}

export function detectAndShowErrors(curProps, nextProps, path) {
    // Bit of a hack but basically if the path is [props.]xyz.errors, this will inspect the value of
    // [props.]xyz.errorModal
    const curErrorModal = getProperty(curProps, `${ path.split('.').slice(0, -1).join('.') }.errorModal`)
    const nextErrorModal = getProperty(nextProps, `${ path.split('.').slice(0, -1).join('.') }.errorModal`)

    // If that errorModal value is set and different to the previous value, we are okay to show a
    // modal. If not set, or has the same value, it means that the errors haven't been updated, so
    // we shouldn't show the modal. It's all very confusing.
    if (! (nextErrorModal && (curErrorModal !== nextErrorModal))) {
        return false
    }

    // Okay, so if we are okay to show a modal, just get all the errors and display them.

    const nextErrors = getLeaves(getProperty(nextProps, path))

    if (nextErrors.length) {
        curProps.modalActions.showModal({
            component: <ErrorModal errors={ nextErrors } />,
            modalProps: {
                allowClose: true,
                closeButton: false,
                error: true,
            },
        })
    }

    return true
}

export function nl2br(str) {
    if (! str) {
        return ''
    }

    const ret = []
    let index = 0

    // go through each line and add it followd by a JSX br to the array
    for (const line of str.split(/\r\n|\n|\r/)) {
        ret.push(line)
        ret.push(<br key={ index++ } />)
    }

    // remove the last br
    ret.pop()

    return ret
}

export const DATE_FORMAT = 'MMMM D, YYYY'

export const jobStatus = {
    PENDING: 'UPCOMING',
    FINISHED: 'SUBMIT_INVOICE',
    OVERTIME_REQUESTED: 'OVERTIME_REQUESTED',
    INVOICED: 'INVOICE_SUBMITTED',
    REJECTED: 'INVOICE_REJECTED',
    RESUBMITTED: 'INVOICE_RESUBMITTED',
    APPROVED: 'INVOICE_APPROVED',
    PAID: 'INVOICE_PAID',
}

export const MEDIA_TYPES = {
    video: 'VIDEO',
    audio: 'AUDIO',
}

export const PHOTO_TYPES = {
    video: 'VIDEO',
    still: 'STILL',
}

export function audioRecorderTypes() {
    return [
        { value: 'RECORDER', label: 'Recorder' },
        { value: 'MIXER', label: 'Mixer' },
        { value: 'BOTH', label: 'Both' },
    ]
}

export function totalCardSpaceOptions() {
    return [
        { value: 32, label: '32GB' },
        { value: 64, label: '64GB' },
        { value: 128, label: '128GB' },
        { value: 256, label: '256GB' },
        { value: 512, label: '512GB' },
        { value: 1024, label: '1TB' },
        { value: 2048, label: '2TB' },
    ]
}

export function ramOptions() {
    return [
        { value: 1, label: '1GB' },
        { value: 2, label: '2GB' },
        { value: 4, label: '4GB' },
        { value: 8, label: '8GB' },
        { value: 16, label: '16GB' },
        { value: 32, label: '32GB' },
        { value: 64, label: '64GB' },
        { value: 128, label: '128GB' },
    ]
}

export function computerTypeOptions() {
    return [
        { value: 'LAPTOP', label: 'Laptop' },
        { value: 'DESKTOP', label: 'Desktop' },
    ]
}

export function computerMakeOptions() {
    return [
        { value: 'APPLE', label: 'Apple' },
        { value: 'PC', label: 'PC' },
    ]
}

export function weightUnitOptions() {
    return [
        { value: 'kg', label: 'kg' },
        { value: 'lb', label: 'lbs' },
    ]
}

export function yesNoOptions() {
    return [
        { value: 1, label: 'Yes' },
        { value: 2, label: 'No' },
    ]
}

export function cameraExperienceOptions() {
    return [
        {
            'value': '16MM',
            'label': '16mm Film',
        },
        {
            'value': '35MM',
            'label': '35mm Film',
        },
        {
            'value': 'ARRI',
            'label': 'Arri Digital Cinema',
        },
        {
            'value': 'PANASONIC',
            'label': 'Panasonic Digital Cinema',
        },
        {
            'value': 'PANAVISION',
            'label': 'Panavision',
        },
        {
            'value': 'PHANTOM',
            'label': 'Phantom High Speed',
        },
        {
            'value': 'RED',
            'label': 'RED Digital Cinema',
        },
        {
            'value': 'SONY',
            'label': 'Sony Digital Cinema',
        },
    ]
}

export function vehiclePassengerOptions() {
    return [
        {
            'value': '0',
            'label': 'No passengers',
        },
        {
            'value': '1',
            'label': '1 passenger',
        },
        {
            'value': '2',
            'label': '2 passengers',
        },
        {
            'value': '3',
            'label': '3 passengers',
        },
        {
            'value': '4',
            'label': '4 passengers',
        },
        {
            'value': '5',
            'label': '5 passengers',
        },
        {
            'value': '6',
            'label': '6 passengers',
        },
    ]
}

export function vehicleDrivingRadiusOptions() {
    return [
        {
            'value': '1H',
            'label': '1 hour',
        },
        {
            'value': '2H',
            'label': '2 hours',
        },
        {
            'value': '3H',
            'label': '3 hours',
        },
        {
            'value': '4H',
            'label': '4 hours',
        },
        {
            'value': '5H',
            'label': '5 hours',
        },
        {
            'value': '6H',
            'label': '6 hours +',
        },
    ]
}

export function smartPhoneOptions() {
    return [
        {
            'value': 'ANDROID',
            'label': 'Android',
        },
        {
            'value': 'APPLE',
            'label': 'Apple',
        },
        {
            'value': 'WINDOWS',
            'label': 'Windows',
        },
        {
            'value': 'BLACKBERRY',
            'label': 'Blackberry',
        },
        {
            'value': 'OTHER',
            'label': 'Other',
        },
    ]
}

export function internetConnectionOptions() {
    return [
        {
            'value': 'DSL',
            'label': 'DSL',
        },
        {
            'value': 'CABLE',
            'label': 'Cable',
        },
        {
            'value': 'FIBER',
            'label': 'Fiber',
        },
        {
            'value': 'OTHER',
            'label': 'Other',
        },
    ]
}



// WEBPACK FOOTER //
// ./app/components/utils.js